import { DOCUMENT } from '@angular/common';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { readThemeCookie, writeThemeCookie } from './theme-cookie';
import { THEME_OPTIONS } from './theme-options.token';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly options = inject(THEME_OPTIONS);
  private readonly media = this.document.defaultView?.matchMedia?.('(prefers-color-scheme: dark)');

  private readonly systemDark = signal(this.media?.matches ?? false);
  private readonly _preference = signal<ThemePreference>(this.readStored());

  readonly preference = this._preference.asReadonly();
  readonly resolvedTheme = computed<ResolvedTheme>(() => {
    const pref = this._preference();
    return pref === 'system' ? (this.systemDark() ? 'dark' : 'light') : pref;
  });

  constructor() {
    this.media?.addEventListener?.('change', e => this.systemDark.set(e.matches));
    effect(() => this.applyTheme(this.resolvedTheme()));
    // A default written on start is indistinguishable from a choice, so 'read-write' stores explicit choices only.
    if (this.options.sharedCookie !== 'read-write') effect(() => this.persist(this._preference()));
  }

  setPreference(preference: ThemePreference): void {
    this._preference.set(preference);
    if (this.options.sharedCookie === 'read-write') this.persist(preference);
    if (this.options.sharedCookie && preference !== 'system') writeThemeCookie(this.document, preference);
  }

  toggle(): void {
    this.setPreference(this.resolvedTheme() === 'dark' ? 'light' : 'dark');
  }

  private applyTheme(theme: ResolvedTheme): void {
    const root = this.document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
    root.style.colorScheme = theme;
  }

  private persist(preference: ThemePreference): void {
    try {
      if (preference === 'system') {
        this.document.defaultView?.localStorage?.removeItem(this.options.storageKey);
      } else {
        this.document.defaultView?.localStorage?.setItem(this.options.storageKey, preference);
      }
    } catch {
      /* storage unavailable (private mode / SSR) — preference stays in-memory */
    }
  }

  // Copy of this read order lives in apps/frontend/photobook/public/theme-init.js — change both together.
  private readStored(): ThemePreference {
    const { defaultPreference, sharedCookie } = this.options;
    if (sharedCookie === 'read-write') {
      const shared = readThemeCookie(this.document);
      if (shared) return shared;
    }

    try {
      const stored = this.document.defaultView?.localStorage?.getItem(this.options.storageKey);
      if (stored === 'light' || stored === 'dark') return stored;
      return stored === 'system' && sharedCookie !== 'read-write' ? stored : defaultPreference;
    } catch {
      return defaultPreference;
    }
  }
}
